import React, { useCallback, useEffect, useState } from "react";
import { compressDataUrl } from "../lib/imageCompress";
import Cropper from "react-easy-crop";
import { Check, RotateCcw, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Slider } from "./ui/slider";

export const OUTPUT_SIZE = 384; // px — plenty for a 96px avatar on retina screens, tiny to store

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = () => reject(new Error("Could not read the image"));
  r.readAsDataURL(file);
});

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error("Could not load the image"));
  img.src = src;
});

/** Draw the selected square onto a canvas and return a JPEG data URL. */
export async function cropToDataUrl(src, area, size = OUTPUT_SIZE) {
  const img = await loadImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, size, size); // PNG transparency -> white, not black
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, size, size);
  return canvas.toDataURL("image/jpeg", 0.88);
}

/**
 * Phase 86 — crop / zoom a chosen photo into a square avatar before upload.
 * `file`: File from an <input type=file>. Calls `onCropped(dataUrl)` with a 384×384 JPEG.
 */
export function PhotoCropDialog({ open, onOpenChange, file, onCropped, title = "Adjust photo" }) {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setSrc(null); setCrop({ x: 0, y: 0 }); setZoom(1); setArea(null); setError("");
    if (file) readAsDataUrl(file).then((d) => { if (alive) setSrc(d); }).catch((e) => setError(e.message));
    return () => { alive = false; };
  }, [file]);

  const onCropComplete = useCallback((_, pixels) => setArea(pixels), []);

  const confirm = async () => {
    if (!src || !area) return;
    setBusy(true);
    try {
      const out = await compressDataUrl(await cropToDataUrl(src, area));   // Phase 130: <= 200 KB guarantee
      await onCropped(out);
      onOpenChange(false);
    } catch (e) {
      setError(e?.message || "Could not crop the image");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-md" data-testid="photo-crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription>Drag to reposition. Use the slider (or pinch / scroll) to zoom. The circle is what everyone will see.</DialogDescription>
        </DialogHeader>
        <div className="relative h-72 w-full overflow-hidden rounded-lg bg-muted" data-testid="photo-crop-area">
          {src ? (
            <Cropper image={src} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false}
              minZoom={1} maxZoom={4} zoomSpeed={0.3} restrictPosition
              onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete}
              style={{ containerStyle: { background: "hsl(var(--muted))" } }} />
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">{error || "Loading image…"}</div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <ZoomOut className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
          <Slider value={[zoom]} min={1} max={4} step={0.05} onValueChange={([z]) => setZoom(z)} aria-label="Zoom" data-testid="photo-crop-zoom" />
          <ZoomIn className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" title="Reset" onClick={() => { setZoom(1); setCrop({ x: 0, y: 0 }); }} data-testid="photo-crop-reset">
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
        {error && src && <p className="text-xs text-destructive" data-testid="photo-crop-error">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="photo-crop-cancel">Cancel</Button>
          <Button type="button" onClick={confirm} disabled={!src || !area || busy} data-testid="photo-crop-confirm">
            <Check className="h-4 w-4 mr-1.5" /> {busy ? "Saving…" : "Use this photo"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
